import { useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";

gsap.registerPlugin(ScrollTrigger);

// ── Skill tiers ──
const TIER = {
  lg: {
    fontSize: "clamp(0.6rem,0.82vw,0.74rem)",
    py: "6px",
    px: "12px",
    weight: 600,
  },
  sm: {
    fontSize: "clamp(0.58rem,0.78vw,0.7rem)",
    py: "6px",
    px: "11px",
    weight: 500,
  },
};

// ── Skills organized by category (from resume) ──
const SKILL_GROUPS = [
  {
    label: "Focus Areas",
    tier: "lg",
    pills: [
      { label: "Full-Stack Engineering", r: -1.5, color: "#0A84FF" },
      { label: "System Design", r: 1.2, color: "#1C1C1E" },
      { label: "AI Engineering", r: -2.0, color: "#7C3AED" },
      { label: "Product Thinking", r: 0.8, color: "#FF9500" },
      { label: "Developer Experience", r: -1.3, color: "#34C759" },
      { label: "Distributed Systems", r: 1.8, color: "#FF9500" },
      { label: "AI Impact & Value", r: -1.3, color: "#34C759" },
    ],
  },
  {
    label: "Frontend",
    tier: "lg",
    pills: [
      { label: "React.js", r: -2.5, color: "#0891B2" },
      { label: "Vue.js", r: 1.8, color: "#2D9E4A" },
      { label: "TypeScript", r: -1.6, color: "#2563EB" },
      { label: "Tailwind CSS", r: 2.2, color: "#0891B2" },
      { label: "GSAP", r: -3.0, color: "#2D9E4A" },
      { label: "Component Architecture", r: 1.5, color: "#3A3A3C" },
      { label: "API Integration", r: 1.8, color: "#2D9E4A" },
    ],
  },
  {
    label: "Backend",
    tier: "lg",
    pills: [
      { label: "Node.js", r: -1.8, color: "#2D9E4A" },
      { label: "Express.js", r: 2.5, color: "#3A3A3C" },
      { label: "NestJS", r: -2.2, color: "#CC2B22" },
      { label: "REST APIs", r: 1.0, color: "#0055CC" },
      { label: "API Design", r: -1.5, color: "#0A84FF" },
      { label: "High Level Design", r: 2.5, color: "#0891B2" },
    ],
  },
  {
    label: "Database",
    tier: "lg",
    pills: [
      { label: "PostgreSQL", r: -2.0, color: "#1E4D8C" },
      { label: "MongoDB", r: 1.5, color: "#2D9E4A" },
      { label: "Prisma ORM", r: -1.2, color: "#3A3A3C" },
    ],
  },
  {
    label: "AI & Tools",
    tier: "lg",
    pills: [
      { label: "Claude Code", r: -2.8, color: "#7C3AED" },
      { label: "GitHub Copilot", r: 1.8, color: "#1C1C1E" },
      { label: "Prompt Engineering", r: -1.5, color: "#0A84FF" },
      { label: "Cursor", r: 2.5, color: "#3A3A3C" },
      { label: "OpenAI Codex", r: -2.0, color: "#0891B2" },
      { label: "Gemini", r: 1.3, color: "#1E4D8C" },
      { label: "Google Stitch", r: -3.0, color: "#2D9E4A" },
    ],
  },
];

// ── Pill ──
const Pill = ({ label, tier, r, color }) => {
  const [hov, setHov] = useState(false);
  const s = TIER[tier];
  const bg = `${color}12`;
  const bdr = `${color}38`;
  return (
    <span
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: `${s.py} ${s.px}`,
        borderRadius: "9999px",
        fontSize: s.fontSize,
        fontFamily: "var(--font-robert-medium)",
        fontWeight: s.weight,
        letterSpacing: "0.025em",
        color,
        background: hov ? `${color}22` : bg,
        border: `1px solid ${hov ? `${color}65` : bdr}`,
        transform: hov ? "rotate(0deg) translateY(-3px)" : `rotate(${r}deg)`,
        boxShadow: hov
          ? `0 8px 22px ${color}25, 0 2px 6px rgba(0,0,0,0.04)`
          : "none",
        transition:
          "transform 0.42s cubic-bezier(0.34,1.56,0.64,1), background 0.25s ease, border-color 0.25s ease, box-shadow 0.3s ease",
        cursor: "default",
        userSelect: "none",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
};

// ── Skill group row ──
const SkillGroup = ({ label, tier, pills }) => (
  <div className="sk-group">
    {/* Category label + rule */}
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        marginBottom: "12px",
      }}
    >
      <span
        className="font-robert-medium uppercase"
        style={{
          flexShrink: 0,
          fontSize: "8px",
          letterSpacing: "0.28em",
          color: "rgba(28,28,30,0.6)",
        }}
      >
        {label}
      </span>
      <div
        style={{ flex: 1, height: "1px", background: "rgba(28,28,30,0.07)" }}
      />
    </div>
    {/* Pills */}
    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 7px" }}>
      {pills.map((p) => (
        <div
          key={p.label}
          className="sk-pill"
          style={{ display: "inline-flex" }}
        >
          <Pill tier={tier} {...p} />
        </div>
      ))}
    </div>
  </div>
);

// ── Experience card ──
const ExperienceCard = () => (
  <div
    className="sk-exp"
    style={{
      borderRadius: "18px",
      background:
        "linear-gradient(140deg, #1C1C1E 0%, #2C2C2E 60%, #242426 100%)",
      border: "1px solid rgba(255,255,255,0.09)",
      boxShadow:
        "0 8px 32px rgba(0,0,0,0.14), inset 0 1px 0 rgba(255,255,255,0.07)",
      padding: "clamp(14px,1.8vw,22px) clamp(16px,2vw,24px)",
      position: "relative",
      overflow: "hidden",
    }}
  >
    {/* Blue top accent */}
    <div
      aria-hidden="true"
      style={{
        position: "absolute",
        top: 0,
        left: "12%",
        right: "12%",
        height: "1.5px",
        background: "linear-gradient(90deg, transparent, #0A84FF, transparent)",
        borderRadius: "9999px",
      }}
    />
    {/* Corner glow */}
    <div
      aria-hidden="true"
      style={{
        position: "absolute",
        top: "-30px",
        right: "-30px",
        width: "120px",
        height: "120px",
        borderRadius: "50%",
        background:
          "radial-gradient(circle, rgba(10,132,255,0.15) 0%, transparent 70%)",
        filter: "blur(20px)",
        pointerEvents: "none",
      }}
    />

    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: "12px",
      }}
    >
      {/* Left: company + role */}
      <div style={{ flex: 1 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "4px",
            flexWrap: "wrap",
          }}
        >
          <p
            className="font-robert-medium"
            style={{
              fontSize: "clamp(0.8rem,1.1vw,0.96rem)",
              fontWeight: 700,
              color: "#F5F5F7",
            }}
          >
            WebEthics Solutions (Path Coders Pvt. Ltd.)
          </p>
          {/* LIVE badge */}
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
              padding: "3px 9px",
              borderRadius: "9999px",
              background: "rgba(52,199,89,0.12)",
              border: "1px solid rgba(52,199,89,0.25)",
              fontSize: "7px",
              color: "#34C759",
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              fontFamily: "var(--font-robert-medium)",
              fontWeight: 600,
            }}
          >
            <span
              style={{
                width: "5px",
                height: "5px",
                borderRadius: "50%",
                background: "#34C759",
                flexShrink: 0,
                animation: "pulse-live 1.6s ease-in-out infinite",
              }}
            />
            Live
          </span>
        </div>

        <p
          className="font-robert-medium"
          style={{
            fontSize: "clamp(0.65rem,0.88vw,0.78rem)",
            color: "#0A84FF",
            fontWeight: 600,
            marginBottom: "3px",
          }}
        >
          Junior Software Developer
        </p>
        <p
          className="font-robert-medium"
          style={{
            fontSize: "clamp(0.56rem,0.72vw,0.64rem)",
            color: "rgba(255,255,255,0.32)",
            letterSpacing: "0.02em",
          }}
        >
          Sept 2025 → Present &nbsp;·&nbsp; IT City, Mohali, Punjab
        </p>
      </div>

      {/* Right: stat */}
      <div style={{ textAlign: "right", flexShrink: 0 }}>
        <p
          className="font-robert-medium"
          style={{
            fontSize: "clamp(1.5rem,2.4vw,2rem)",
            fontWeight: 700,
            color: "#0A84FF",
            lineHeight: 1,
            letterSpacing: "-0.03em",
          }}
        >
          3
        </p>
        <p
          className="font-robert-medium uppercase"
          style={{
            fontSize: "6.5px",
            color: "rgba(255,255,255,0.28)",
            letterSpacing: "0.2em",
            marginTop: "3px",
          }}
        >
          Products
        </p>
      </div>
    </div>

    {/* Achievement bullets */}
    <div
      style={{
        marginTop: "12px",
        paddingTop: "10px",
        borderTop: "1px solid rgba(255,255,255,0.06)",
        display: "flex",
        flexDirection: "column",
        gap: "7px",
      }}
    >
      {[
        "Owned and shipped end-to-end features for a production-grade cultural discovery platform serving real users in Chicago — translating business requirements into scalable user-facing experiences and backend services.",
        "Built internal tooling, integrated Stripe payments & webhooks, optimised application performance, and leveraged AI-assisted development workflows to accelerate delivery without sacrificing code quality or ownership.",
      ].map((line, i) => (
        <div
          key={i}
          style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}
        >
          <span
            style={{
              flexShrink: 0,
              marginTop: "4px",
              width: "4px",
              height: "4px",
              borderRadius: "50%",
              background: "#0A84FF",
              opacity: 0.7,
            }}
          />
          <p
            className="font-robert-medium"
            style={{
              fontSize: "clamp(0.54rem,0.72vw,0.64rem)",
              color: "rgba(255,255,255,0.38)",
              lineHeight: 1.6,
              letterSpacing: "0.01em",
            }}
          >
            {line}
          </p>
        </div>
      ))}
    </div>

    {/* Intern → Full-time promotion banner */}
    <div
      style={{
        marginTop: "10px",
        borderRadius: "10px",
        background:
          "linear-gradient(100deg, rgba(52,199,89,0.10) 0%, rgba(10,132,255,0.08) 100%)",
        border: "1px solid rgba(52,199,89,0.22)",
        padding: "8px 12px",
        display: "flex",
        alignItems: "center",
        gap: "10px",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* subtle left accent line */}
      <div
        style={{
          position: "absolute",
          left: 0,
          top: "15%",
          bottom: "15%",
          width: "2.5px",
          borderRadius: "9999px",
          background: "linear-gradient(180deg, #34C759, #0A84FF)",
        }}
      />

      {/* Trophy / star icon */}
      <span
        style={{
          fontSize: "clamp(0.75rem,1vw,0.9rem)",
          flexShrink: 0,
          marginLeft: "6px",
        }}
      >
        🏆
      </span>

      <div style={{ flex: 1 }}>
        <p
          className="font-robert-medium uppercase"
          style={{
            fontSize: "clamp(0.48rem,0.62vw,0.55rem)",
            color: "#34C759",
            letterSpacing: "0.22em",
            fontWeight: 700,
            marginBottom: "2px",
          }}
        >
          Promoted to Full-Time
        </p>
        <p
          className="font-robert-medium"
          style={{
            fontSize: "clamp(0.52rem,0.68vw,0.6rem)",
            color: "rgba(255,255,255,0.52)",
            letterSpacing: "0.01em",
            lineHeight: 1.4,
          }}
        >
          Recognised for impact as Intern &nbsp;·&nbsp; Jun 2025 – Sept 2025
        </p>
      </div>

      {/* Arrow badge */}
      <span
        style={{
          flexShrink: 0,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: "22px",
          height: "22px",
          borderRadius: "50%",
          background: "rgba(52,199,89,0.15)",
          border: "1px solid rgba(52,199,89,0.3)",
          color: "#34C759",
          fontSize: "11px",
          fontWeight: 700,
        }}
      >
        ↑
      </span>
    </div>
  </div>
);

// ── Section ──
const Skills = () => {
  const sectionRef = useRef(null);

  useGSAP(
    () => {
      const pref = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;
      if (pref) return;

      gsap.set(".sk-hdr", { opacity: 0, y: -10 });
      gsap.set(".sk-exp", { opacity: 0, y: 16 });
      gsap.set(".sk-group", { opacity: 0, x: -20 });
      gsap.set(".sk-pill", { opacity: 0, y: 8, scale: 0.9 });
      gsap.set(".sk-avatar-wrap", { opacity: 0, x: 32 });

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 62%",
          once: true,
        },
      });

      tl.to(".sk-hdr", { opacity: 1, y: 0, duration: 0.5, ease: "power3.out" })
        .to(
          ".sk-exp",
          { opacity: 1, y: 0, duration: 0.7, ease: "expo.out" },
          "-=0.25",
        )
        .to(
          ".sk-group",
          {
            opacity: 1,
            x: 0,
            duration: 0.55,
            stagger: 0.08,
            ease: "power2.out",
          },
          "-=0.4",
        )
        .to(
          ".sk-avatar-wrap",
          { opacity: 1, x: 0, duration: 1.0, ease: "expo.out" },
          "-=0.7",
        )
        .to(
          ".sk-pill",
          {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 0.4,
            stagger: 0.028,
            ease: "back.out(1.6)",
          },
          "-=0.65",
        );
    },
    { scope: sectionRef },
  );

  return (
    <section
      ref={sectionRef}
      id="skills"
      className="overflow-visible min-h-[100dvh] md:h-dvh md:overflow-hidden"
      style={{
        background: "#F8F7F5",
        display: "grid",
        gridTemplateRows: "auto 1fr",
        padding:
          "clamp(20px,3.8vw,48px) clamp(12px,3.5vw,48px) clamp(28px,6vw,80px)",
        gap: "clamp(12px,1.6vw,18px)",
      }}
    >
      {/* ── Header ── */}
      <div className="sk-hdr flex items-center justify-between">
        <p
          className="font-robert-medium uppercase"
          style={{
            fontSize: "12px",
            color: "rgba(28,28,30,0.38)",
            letterSpacing: "0.3em",
          }}
        >
          Experience &amp; Skills
        </p>
        <p
          className="font-robert-medium uppercase"
          style={{
            fontSize: "12px",
            color: "rgba(28,28,30,0.38)",
            letterSpacing: "0.22em",
          }}
        >
          Full-Stack · AI · Systems
        </p>
      </div>

      {/* ── Body ── */}
      {/*
        3 flat grid children — DOM order drives mobile stacking:
          1. ExperienceCard  → mobile row 1 | desktop col 1 row 1 (auto)
          2. Avatar          → mobile row 2 | desktop col 2 rows 1-2 (explicit)
          3. Skill groups    → mobile row 3 | desktop col 1 row 2 (auto)
      */}
      <div
        className="grid grid-cols-1 gap-3 md:[grid-template-columns:1.08fr_0.92fr] md:[grid-template-rows:auto_1fr] md:gap-x-[clamp(16px,2.8vw,40px)] md:gap-y-[clamp(10px,1.4vw,16px)] md:h-full"
        style={{ overflow: "visible" }}
      >
        {/* ─── 1: Experience card ─── */}
        <ExperienceCard />

        {/* ─── 2: Avatar — explicit desktop placement keeps col 2 across both rows ─── */}
        <div
          className="sk-avatar-wrap relative flex items-center justify-center overflow-visible pt-4 md:col-start-2 md:row-start-1 md:row-span-2 md:items-end md:overflow-hidden md:pt-[clamp(16px,3vh,36px)]"
        >
          {/* Blue ambient */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              top: "0%",
              left: "5%",
              width: "88%",
              height: "58%",
              background:
                "radial-gradient(ellipse, rgba(10,132,255,0.1) 0%, transparent 70%)",
              filter: "blur(56px)",
              pointerEvents: "none",
            }}
          />
          {/* Violet ambient */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              bottom: "0%",
              right: "-5%",
              width: "70%",
              height: "50%",
              background:
                "radial-gradient(ellipse, rgba(124,58,237,0.07) 0%, transparent 70%)",
              filter: "blur(48px)",
              pointerEvents: "none",
            }}
          />
          {/* Ground shadow */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              bottom: "0",
              left: "10%",
              right: "10%",
              height: "40px",
              background:
                "radial-gradient(ellipse, rgba(10,132,255,0.12) 0%, transparent 70%)",
              filter: "blur(16px)",
              pointerEvents: "none",
            }}
          />

          <img
            src="/img/book_reading.png"
            alt="Vishvjeet Rana"
            draggable={false}
            className="relative z-[1] w-auto h-[clamp(220px,40vh,300px)] md:h-[clamp(360px,82vh,840px)]"
            style={{
              objectFit: "contain",
              mixBlendMode: "multiply",
              animation: "gentle-float 4.4s ease-in-out infinite",
              filter: "drop-shadow(0 20px 44px rgba(10,132,255,0.1))",
              userSelect: "none",
            }}
          />
        </div>

        {/* ─── 3: Skill groups — auto-placed at col 1 row 2 on desktop ─── */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "clamp(4px,0.6vw,7px)",
            overflow: "hidden",
            padding: "8px 10px",
            borderRadius: "14px",
            background: "rgba(28,28,30,0.03)",
            border: "1px solid rgba(28,28,30,0.06)",
          }}
        >
          {SKILL_GROUPS.map((g) => (
            <SkillGroup key={g.label} {...g} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Skills;
